/**
 * O catálogo de telas que o navegador conhece — id, rota e permissão de leitura.
 *
 * **Não é o menu.** O menu inteiro vem do servidor, no manifesto: é ele que decide grupo,
 * rótulo, ícone, ordem e o que cada pessoa alcança, e a tela só desenha o que chegou de lá
 * (`useMenuConfig`). Este arquivo já foi a cópia local disso — grupos, rótulos por afinidade,
 * ícones e pesos —, e a cópia não era lida por ninguém: quem editasse o rótulo aqui veria a
 * tela continuar igual, porque o rótulo que aparece é o do servidor.
 *
 * O que sobrou tem um uso só, e é um uso que o manifesto **não** cobre: o manifesto traz o que
 * a pessoa alcança, e aqui está o que existe. É a diferença entre as duas listas que permite a
 * guarda dizer "esta tela não faz parte do seu acesso" em vez de deixar passar por omissão
 * (`avaliar`, em `authorization/pode.ts`). O estado vazio das listagens usa o mesmo id para
 * achar a rota e a família de permissão do próximo passo (`EmptyState`).
 *
 * Tela nova de menu entra aqui com id, rota e a permissão de ler. Rótulo e posição, não: quem
 * responde por eles é o servidor.
 */

import { TelaDoCatalogo } from '../types/menuConfig';

export const CATALOGO_DE_TELAS: readonly TelaDoCatalogo[] = [
  { id: 'companies', caminho: '/companies', permissao: 'company.read' },
  { id: 'departments', caminho: '/departments', permissao: 'department.read' },
  { id: 'units', caminho: '/units', permissao: 'unit.read' },
  { id: 'occupations', caminho: '/occupations', permissao: 'occupation.read' },
  { id: 'employees', caminho: '/employees', permissao: 'employee.read' },
  { id: 'providers', caminho: '/providers', permissao: 'provider.read' },
  { id: 'professionals', caminho: '/professionals', permissao: 'professional.read' },
  { id: 'standard-texts', caminho: '/standard-texts', permissao: 'standard_text.read' },
  { id: 'training-types', caminho: '/training-types', permissao: 'training-type.read' },
  { id: 'appointments', caminho: '/appointments', permissao: 'appointment.read' },
  { id: 'schedule-slots', caminho: '/schedule-slots', permissao: 'schedule_slot.read' },
  { id: 'professional-schedules', caminho: '/professional-schedules', permissao: 'professional_schedule.read' },
  { id: 'reception', caminho: '/reception', permissao: 'reception.read' },
  { id: 'medical-consultations', caminho: '/medical-consultations', permissao: 'consultation.read' },
  { id: 'occupational-exams', caminho: '/occupational-exams', permissao: 'occupational_exam.read' },
  { id: 'toxicological-exams', caminho: '/exames-toxicologicos', permissao: 'toxicological_exam.read' },
  { id: 'medical-leaves', caminho: '/medical-leaves', permissao: 'medical_leaves.read' },
  { id: 'absenteeism', caminho: '/absenteismo', permissao: 'medical_leaves.read' },
  { id: 'consultation-history', caminho: '/consultation-history', permissao: 'consultation.read' },
  { id: 'exam-summons', caminho: '/exam-summons', permissao: 'exam_summons.read' },
  { id: 'vaccination-pendencies', caminho: '/vaccination-pendencies', permissao: 'vaccination.read' },
  { id: 'vaccine-types', caminho: '/vaccine-types', permissao: 'vaccine-type.read' },
  { id: 'disabilities', caminho: '/disabilities', permissao: 'disability.read' },
  { id: 'epis', caminho: '/epis', permissao: 'epi.read' },
  { id: 'epcs', caminho: '/epcs', permissao: 'epc.read' },
  { id: 'occupational-risks', caminho: '/occupational-risks', permissao: 'occupational_risk.read' },
  { id: 'source-generators', caminho: '/source-generators', permissao: 'source_generator.read' },
  { id: 'machines', caminho: '/machines', permissao: 'machine.read' },
  { id: 'ergonomic-workstations', caminho: '/ergonomic-workstations', permissao: 'ergonomic_workstation.read' },
  { id: 'electrical-installations', caminho: '/electrical-installations', permissao: 'electrical_installation.read' },
  { id: 'fire-equipments', caminho: '/fire-equipments', permissao: 'fire_equipment.read' },
  { id: 'flammable-installations', caminho: '/flammable-installations', permissao: 'flammable_installation.read' },
  { id: 'work-fronts', caminho: '/work-fronts', permissao: 'work_front.read' },
  { id: 'application-groups', caminho: '/application-groups', permissao: 'application_group.read' },
  { id: 'cats', caminho: '/cats', permissao: 'cat.read' },
  { id: 'hazardous-agents', caminho: '/hazardous-agents', permissao: 'hazardous_agent.read' },
  { id: 'measurement-equipments', caminho: '/measurement-equipments', permissao: 'measurement_equipment.read' },
  { id: 'environmental-measurements', caminho: '/environmental-measurements', permissao: 'environmental_measurement.read' },
  { id: 'trainings', caminho: '/trainings', permissao: 'training.read' },
  { id: 'cipaa', caminho: '/cipaa', permissao: 'cipaa.read' },
  { id: 'psychosocial', caminho: '/psychosocial', permissao: 'psychosocial.read' },
  { id: 'certificates', caminho: '/certificates', permissao: 'certificate.read' },
  { id: 'risk-exam-migration', caminho: '/risk-exam-migration', permissao: 'risk_exam_migration.read' },
  { id: 'document-history', caminho: '/documents/history', permissao: 'document_emission.read' },
  { id: 'document-emission', caminho: '/documents/history/emitir', permissao: 'document_emission.read' },
  { id: 'document-attachments', caminho: '/document-attachments', permissao: 'document-attachment.read' },
  { id: 'action-plan', caminho: '/action-plan', permissao: 'action_plan.read' },
  { id: 'users', caminho: '/users', permissao: 'user.read' },
  { id: 'roles', caminho: '/roles', permissao: 'role.read' },
  { id: 'permissions', caminho: '/permissions', permissao: 'permission.read' },
  { id: 'holidays', caminho: '/holidays', permissao: 'holiday.read' },
  { id: 'cid-codes', caminho: '/cid-codes', permissao: 'cid-code.read' },
  { id: 'whatsapp', caminho: '/whatsapp', permissao: 'whatsapp.read' },
  { id: 'email-config', caminho: '/settings/email', permissao: 'email.config.manage' },
  { id: 'proposals', caminho: '/proposals', permissao: 'proposal.read' },
  { id: 'consultants', caminho: '/consultants', permissao: 'consultant.read' },
  { id: 'financial-dashboard', caminho: '/financial', permissao: 'invoices.view' },
  { id: 'invoices', caminho: '/financial/invoices', permissao: 'invoices.view' },
  { id: 'platform-fees', caminho: '/financial/platform-fees', permissao: 'platform-fees.view' },
];
